import { IExcelReportData, ITableData } from 'types/excelExport';
import { isDebtSource } from 'utils/sourceTypeUtils';
import {
    createBlankRow,
    createDarkSectionHeader,
    createDataRow,
    createHeaderRow,
    createMetricRow,
    parseNumber,
} from '../helpers';
import { EXCEL_COLORS, EXCEL_NUM_FORMATS } from '../constants';
import { LD_LABELS } from './constants';
import { ILdReturnsSummarySheetParams } from './types';

const createSectionHeader = createDarkSectionHeader;

const deriveProjectEconomics = (
    returns: ILdReturnsSummarySheetParams['returns'],
    disposition: ILdReturnsSummarySheetParams['disposition'],
    totalLots: number | null | undefined,
) => {
    const grossSaleProceeds = parseNumber(returns.totalSaleProceeds) ?? 0;
    const closingCostsPct = disposition?.closingCostsPercentage ?? 0;
    const closingCosts = parseNumber(returns.closingCosts)
        ?? (grossSaleProceeds * closingCostsPct);
    const netSaleProceeds = parseNumber(returns.netSaleProceedsAfterClosingCosts)
        ?? (grossSaleProceeds - closingCosts);
    const totalOtherRevenue = parseNumber(returns.totalOtherRevenue) ?? 0;
    const totalNetRevenue = netSaleProceeds + totalOtherRevenue;
    const totalProjectCost = parseNumber(returns.totalProjectCost) ?? 0;

    const projectProfit = returns.projectProfit
        ?? (totalNetRevenue - totalProjectCost);
    const projectProfitMargin = returns.projectProfitMargin
        ?? (totalProjectCost > 0 ? projectProfit / totalProjectCost : null);
    const profitPerLot = totalLots && totalLots > 0
        ? projectProfit / totalLots
        : null;

    return {
        projectProfit,
        projectProfitMargin,
        profitPerLot,
        netSaleProceeds,
        totalOtherRevenue,
        totalNetRevenue,
    };
};

const deriveCapitalStack = (
    sourcesSummary: ILdReturnsSummarySheetParams['sourcesSummary'],
    sourceTypes: ILdReturnsSummarySheetParams['sourceTypes'],
) => {
    const sources = sourcesSummary?.sources ?? [];
    let constructionLoan = 0;
    let totalEquity = 0;
    sources.forEach(source => {
        const amount = parseNumber(source.amount) ?? 0;
        if (isDebtSource(source.sourceTypeId, sourceTypes)) {
            constructionLoan += amount;
        } else {
            totalEquity += amount;
        }
    });

    return { constructionLoan, totalEquity };
};

export const formatLdReturnsSummarySheet = ({
    dealName,
    proForma,
    returns,
    disposition,
    sourcesSummary,
    sourceTypes,
    showCents = false,
}: ILdReturnsSummarySheetParams): IExcelReportData => {
    const labels = LD_LABELS.returns;
    const currencyFmt = showCents ? EXCEL_NUM_FORMATS.CURRENCY_CENTS : EXCEL_NUM_FORMATS.CURRENCY;
    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const {
        projectProfit,
        projectProfitMargin,
        profitPerLot,
        netSaleProceeds,
        totalOtherRevenue,
        totalNetRevenue,
    } = deriveProjectEconomics(
        returns,
        disposition,
        proForma.totalLots,
    );
    const { constructionLoan, totalEquity } = deriveCapitalStack(sourcesSummary, sourceTypes);

    const topHeaderRows: ITableData[] = [
        createDataRow([{ value: dealName, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: labels.title, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(3),
    ];

    const propertyReturnsHeader = createSectionHeader(labels.projectReturns, 3);
    const propertyReturnsColumnHeader = createHeaderRow(
        [labels.metric, labels.unlevered, labels.levered],
        EXCEL_COLORS.BLUE_HEADER,
        true,
    );

    const propertyReturnsRows: ITableData[] = [
        createMetricRow(labels.irr, [
            returns.netUnleveredIrr,
            returns.netLeveredIrr,
        ], EXCEL_NUM_FORMATS.PERCENT),
        createMetricRow(labels.equityMultiple, [
            parseNumber(returns.netUnleveredEquityMultiple),
            returns.netLeveredEquityMultiple,
        ], EXCEL_NUM_FORMATS.MULTIPLE),
        createMetricRow(labels.roi, [
            returns.unleveredRoi,
            returns.leveredRoi,
        ], EXCEL_NUM_FORMATS.PERCENT),
        createMetricRow(labels.netProfit, [
            parseNumber(returns.netUnleveredNetProfit),
            parseNumber(returns.netLeveredNetProfit),
        ], currencyFmt),
    ];

    const economicsHeader = createSectionHeader(labels.projectEconomics, 2);
    const economicsRows: ITableData[] = [
        createDataRow([
            { value: labels.projectProfit, width: 30 },
            {
                value: projectProfit,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: currencyFmt,
            },
        ]),
        createDataRow([
            { value: labels.projectProfitMargin, width: 30 },
            {
                value: projectProfitMargin ?? 0,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: EXCEL_NUM_FORMATS.PERCENT,
            },
        ]),
        createDataRow([
            { value: labels.profitPerLot, width: 30 },
            {
                value: profitPerLot ?? 0,
                width: 15,
                alignment: { horizontal: 'right' },
                numFmt: currencyFmt,
            },
        ]),
    ];

    const keyStatsHeader = createSectionHeader(labels.keyStatistics, 2);
    const createKeyStatRow = (label: string, value: number | null | undefined): ITableData => createDataRow([
        { value: label, width: 30 },
        {
            value: value ?? 0,
            width: 15,
            alignment: { horizontal: 'right' },
            numFmt: currencyFmt,
        },
    ]);
    const keyStatsRows: ITableData[] = [
        createKeyStatRow(labels.totalDevelopmentCost, parseNumber(returns.totalProjectCost)),
        createKeyStatRow(labels.totalNetRevenue, totalNetRevenue),
        createKeyStatRow(labels.netSaleProceeds, netSaleProceeds),
        createKeyStatRow(labels.otherRevenue, totalOtherRevenue),
        createKeyStatRow(labels.totalEquity, totalEquity),
        createKeyStatRow(labels.constructionLoan, constructionLoan),
    ];

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [propertyReturnsHeader, propertyReturnsColumnHeader],
                tableData: propertyReturnsRows,
            },
            {
                tableTitleRows: [createBlankRow(3)],
                tableHeaderRows: [economicsHeader],
                tableData: economicsRows,
            },
            {
                tableTitleRows: [createBlankRow(2)],
                tableHeaderRows: [keyStatsHeader],
                tableData: keyStatsRows,
            },
        ],
    };
};
